import {
  useFaultStudyCombined,
  useLatestFaultStudy,
  usePersistFaultStudy,
} from '../../hooks/useFaultAnalysis.ts'
import { diffFaultStudies, type FaultNodeDiff } from '../../utils/faultStudyDiff.ts'
import { AsOfStamp } from './AsOfStamp.tsx'
import { friendlyError } from '../../utils/friendlyError.ts'

interface FaultStudyCompareProps {
  feederId: number
}

const STATUS_LABEL: Record<FaultNodeDiff['status'], string> = {
  unchanged: 'Unchanged',
  changed: 'Changed',
  added: 'Added',
  removed: 'Removed',
}

const STATUS_COLOR: Record<FaultNodeDiff['status'], string> = {
  unchanged: '#95a5a6',
  changed: '#f1c40f',
  added: '#3498db',
  removed: '#e74c3c',
}

function fmtKa(v: number | null): string {
  return v === null ? '—' : v.toFixed(2)
}

function fmtDelta(v: number | null): string {
  if (v === null) return '—'
  const sign = v > 0 ? '+' : ''
  return `${sign}${v.toFixed(2)}`
}

function deltaColor(v: number | null): string | undefined {
  if (v === null || v === 0) return undefined
  // A rising fault current (topology drift toward higher duty) is the worrying
  // direction — flag it red; a drop reads green.
  return v > 0 ? '#e74c3c' : '#2ecc71'
}

function fmtTimestamp(ts: string | null): string {
  if (!ts) return 'an unknown time'
  const d = new Date(ts)
  return isNaN(d.getTime()) ? ts : d.toLocaleString()
}

function StatChip({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
      <div className="text-2xl font-bold" style={{ color }}>{value}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
    </div>
  )
}

export function FaultStudyCompare({ feederId }: FaultStudyCompareProps) {
  const study = useFaultStudyCombined(feederId, r => r.study)
  const latest = useLatestFaultStudy(feederId)
  const persist = usePersistFaultStudy(feederId)

  const persistButton = (
    <button
      type="button"
      onClick={() => persist.mutate()}
      disabled={persist.isPending}
      className="text-sm px-4 py-1.5 rounded-md bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
    >
      {persist.isPending && (
        <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
      )}
      {persist.isPending ? 'Saving...' : persist.isSuccess ? 'Saved' : 'Persist current study as snapshot'}
    </button>
  )

  const header = (
    <div className="flex items-center justify-between gap-3">
      <h3 className="text-sm font-semibold text-gray-700">Latest vs Live Compare</h3>
      {persistButton}
    </div>
  )

  if (study.isLoading || latest.isLoading) {
    return (
      <div className="space-y-4">
        {header}
        <div className="text-sm text-gray-400 p-2">Loading compare...</div>
      </div>
    )
  }

  // §9: a failed query renders distinctly from an empty result.
  if (study.error || latest.error) {
    const e = study.error ?? latest.error
    return (
      <div className="space-y-4">
        {header}
        <div className="text-sm text-red-600 p-2">
          {friendlyError(e, 'load the compare data')} This is a request error,
          not an empty feeder.
        </div>
      </div>
    )
  }

  if (!study.data || !latest.data) {
    return (
      <div className="space-y-4">
        {header}
        <div className="text-sm text-gray-400 p-2">No study data available</div>
      </div>
    )
  }

  const snapshot = latest.data
  const hasSnapshot = snapshot.node_count > 0

  // §9 degraded-explicit: an empty snapshot is a distinct, actionable state —
  // never an error and never a diff of everything-added.
  if (!hasSnapshot) {
    return (
      <div className="space-y-4">
        {header}
        {persist.isSuccess && (
          <div className="text-xs rounded border border-green-200 bg-green-50 text-green-800 px-3 py-1.5">
            Saved — the live study is now the persisted snapshot.
          </div>
        )}
        {persist.isError && (
          <div className="text-xs rounded border border-red-200 bg-red-50 text-red-700 px-3 py-1.5">
            {friendlyError(persist.error, 'save the snapshot')}
          </div>
        )}
        <div className="text-sm rounded border border-gray-200 bg-gray-50 text-gray-600 px-3 py-3">
          No saved snapshot — persist the current study to enable comparison.
        </div>
      </div>
    )
  }

  const diff = diffFaultStudies(study.data.nodes, snapshot.nodes)
  const counts = {
    changed: diff.filter(d => d.status === 'changed').length,
    added: diff.filter(d => d.status === 'added').length,
    removed: diff.filter(d => d.status === 'removed').length,
    unchanged: diff.filter(d => d.status === 'unchanged').length,
  }
  const drift = diff.filter(d => d.status !== 'unchanged')

  return (
    <div className="space-y-4">
      {header}
      <AsOfStamp computedAt={study.data.analysis_timestamp} />

      {/* Which side is which (live recompute vs the persisted snapshot). */}
      <div className="text-xs text-gray-500">
        Comparing the <span className="font-medium text-gray-700">live</span> recompute against the{' '}
        <span className="font-medium text-gray-700">persisted</span> snapshot from{' '}
        {fmtTimestamp(snapshot.analysis_timestamp)}.
      </div>

      {persist.isSuccess && (
        <div className="text-xs rounded border border-green-200 bg-green-50 text-green-800 px-3 py-1.5">
          Saved — the live study is now the persisted snapshot.
        </div>
      )}

      {persist.isError && (
        <div className="text-xs rounded border border-red-200 bg-red-50 text-red-700 px-3 py-1.5">
          {friendlyError(persist.error, 'save the snapshot')}
        </div>
      )}

      {/* Drift summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatChip label="Changed" value={counts.changed} color={STATUS_COLOR.changed} />
        <StatChip label="Added" value={counts.added} color={STATUS_COLOR.added} />
        <StatChip label="Removed" value={counts.removed} color={STATUS_COLOR.removed} />
        <StatChip label="Unchanged" value={counts.unchanged} color={STATUS_COLOR.unchanged} />
      </div>

      {drift.length === 0 ? (
        <div className="text-sm text-gray-500 p-2">
          No drift — every node matches the persisted snapshot within tolerance.
        </div>
      ) : (
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
            Drifted Nodes ({drift.length})
          </h4>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-gray-200 text-left text-gray-500">
                  <th className="pb-1 pr-2">Node</th>
                  <th className="pb-1 pr-2">Status</th>
                  <th className="pb-1 pr-2 text-right">Live (kA)</th>
                  <th className="pb-1 pr-2 text-right">Persisted (kA)</th>
                  <th className="pb-1 text-right">&Delta; 3-Ph (kA)</th>
                </tr>
              </thead>
              <tbody>
                {drift.map(d => (
                  <tr key={d.node_name} className="border-b border-gray-50">
                    <td className="py-1 pr-2 text-gray-700 font-medium">{d.node_name}</td>
                    <td className="py-1 pr-2">
                      <span
                        className="inline-block px-1.5 py-0.5 rounded text-xs font-medium"
                        style={{
                          backgroundColor: STATUS_COLOR[d.status] + '20',
                          color: STATUS_COLOR[d.status],
                        }}
                      >
                        {STATUS_LABEL[d.status]}
                      </span>
                    </td>
                    <td className="py-1 pr-2 text-right text-gray-600">{fmtKa(d.live_3ph_ka)}</td>
                    <td className="py-1 pr-2 text-right text-gray-600">{fmtKa(d.latest_3ph_ka)}</td>
                    <td
                      className="py-1 text-right font-medium"
                      style={{ color: deltaColor(d.delta_3ph_ka) }}
                    >
                      {fmtDelta(d.delta_3ph_ka)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
